// src/components/landing/Hero.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors } from '../../styles/designSystem';
import {  X } from 'lucide-react';
import posthog from 'posthog-js';
import { DialogForm } from '../ui/DialogForm';


const loadYouTubeVideo = (element) => {
  const videoId = "Btoyt7JFtYg"; // Replace with your video ID
  const iframe = document.createElement("iframe");
  iframe.setAttribute("src", `https://www.youtube.com/embed/${videoId}?autoplay=1`);
  iframe.setAttribute(
    "allow",
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
  );
  iframe.setAttribute("allowfullscreen", "true");
  iframe.setAttribute("frameborder", "0");
  iframe.style.width = "100%";
  iframe.style.height = "100%";
  iframe.style.border = "none";
  iframe.style.position = "absolute"; // Ensure it occupies the full frame
  iframe.style.top = "0";
  iframe.style.left = "0";
  element.innerHTML = ""; // Clear the placeholder
  element.appendChild(iframe);
};

export const Hero = () => {
  const { t  } = useTranslation();
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const response = await fetch('https://formspree.io/f/xovvpnyb', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setShowThankYou(true);
        e.target.reset();
        // Track successful demo request
        posthog.capture('demo_request', {
          variant: variant
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (buttonType) => {
    posthog.capture('button_click', { button_type: buttonType  });
  
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        'event_category': 'engagement',
        'event_label': buttonType
      });
    }
  };
  
  const handleGetStarted = () => {
    document.body.style.opacity = '0.8';
    setTimeout(() => {
      navigate('/onboarding');
      document.body.style.opacity = '1';
    }, 200);
  };

  const handleWatchDemo = (e) => {
    e.preventDefault();
    setShowVideo(true);
  };

  const [title, setTitle] = useState('');
  const [variant, setVariant] = useState('control');

  useEffect(() => {
    const updateTitle = () => {
      const activeVariant = posthog.getFeatureFlag('hero_title_test') || 'control';
      const payload = posthog.getFeatureFlagPayload('hero_title_test');
  
      if (payload && payload.title) {
        setTitle(payload.title);
      } else {
        //setTitle('Default Title – No Variant Selected');
        setTitle(t('hero.title')); // Using the translated description as default

      }
      setVariant(activeVariant);
    };
  
    posthog.onFeatureFlags(updateTitle);
    
    // Initial check
    updateTitle();
  
    return () => {
      posthog.onFeatureFlags(updateTitle);
    };
  }, [t]);


  


  return (
    <section id="hero" className="bg-gradient-to-b from-gray-50 to-gray-100 relative">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          {/* Content Side */}
          <div className="px-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex-grow">
                <h1 
                  className={`${colors.text.heading} ${colors.primary.text} mb-6 text-4xl lg:text-5xl
                    animate-fade-in-up`}
                    style={{ paddingBottom: '0.5rem' }} // Add padding to prevent clipping

                >
                  {title}
                </h1>
                <p 
                  className={`${colors.text.body} text-lg mb-8 animate-fade-in-up`}
                  style={{ animationDelay: '0.2s' }}
                >
                  {t('hero.description')}
                </p>
                <div 
                  className="flex items-center space-x-4 mb-8 animate-fade-in-up"
                  style={{ animationDelay: '0.4s' }}
                >                  
                  <DialogForm
                    triggerText={t('App.book_demo')}
                    title={t('App.book_demo')}
                    description="Schedule a personalized demo with our team"
                    onSubmit={handleDemoSubmit}
                    onClick={() => handleButtonClick('demo_button')}

                  />
                  <DialogForm
                    triggerText={t('App.get_free_trial')}
                    title={t('App.get_free_trial')}
                    description="Start your free trial today"
                    onSubmit={handleDemoSubmit}
                    onClick={() => handleButtonClick('trial_button')}
                    buttonStyle="black"
                  />
                </div>
              </div>
              
              {/* Stats Section */}
              <div 
                className="grid grid-cols-2 gap-6 animate-fade-in-up"
                style={{ animationDelay: '0.6s' }}
              >
                <div 
                  className={`${colors.container.base} p-4 text-center transform 
                    hover:scale-105 transition-all duration-200`}
                >
                  <div className={`${colors.primary.text} font-bold text-2xl`}>
                    {t('hero.stats.availability.value')}
                  </div>
                  <div className={colors.text.subtitle}>
                    {t('hero.stats.availability.label')}
                  </div>
                </div>
                <div 
                  className={`${colors.container.base} p-4 text-center transform 
                    hover:scale-105 transition-all duration-200`}
                >
                  <div className={`${colors.primary.text} font-bold text-2xl`}>
                    {t('hero.stats.call_coverage.value')}
                  </div>
                  <div className={colors.text.subtitle}>
                    {t('hero.stats.call_coverage.label')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video Side */}
          <div className="relative group px-4 h-full flex items-center animate-fade-in-up"
     style={{ animationDelay: '0.8s' }}>
  <div className="w-full py-8">
    <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 
      to-indigo-500/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 
      transition-opacity duration-300" />

    <div className="relative overflow-hidden aspect-video rounded-xl cursor-pointer video-facade"
         role="button"
         tabIndex="0"
         aria-label={t('hero.video.title')}
         onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && loadYouTubeVideo(e.currentTarget)}
         onClick={(e) => loadYouTubeVideo(e.currentTarget)}>
      <img
        className="absolute w-full h-full object-cover"
        src="https://img.youtube.com/vi/Btoyt7JFtYg/hqdefault.jpg"
        alt={t('hero.video.title')}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="play-button" />
      </div>
    </div>
  </div>
</div>

        </div>
      </div>

      {/* Full-screen video modal */}
      {showVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center 
            animate-fade-in backdrop-blur-sm"
        >
          <div className="relative w-full max-w-4xl mx-4">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 
                transition-colors"
            >
              <X size={24} />
            </button>
            <div className="aspect-video relative rounded-xl overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full absolute inset-0"
                src="https://www.youtube.com/embed/Btoyt7JFtYg?autoplay=1"
                title={t('hero.video.title')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
